import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Info,
  Euro,
  BarChart3,
  Users,
  AlertCircle,
  ArrowRight,
  Target,
  FolderGit2,
  Clock,
  GitBranch,
  Eye,
  Code2,
  Globe,
  Box,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const PLAN_COLORS = ["#0099CC", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#3B82F6"];

function Trend({ pct }) {
  const up = pct >= 0;
  return (
    <span className={`text-xs font-medium inline-flex items-center gap-1 ${up ? "text-[#10B981]" : "text-[#EF4444]"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function KpiCard({ title, value, trendPct, subtitle, color = "#0099CC" }) {
  return (
    <div className="bg-[#151B2B] rounded-xl p-6 border border-[#2A3441] card-premium">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[#94A3B8] text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-2">{value}</p>
          {subtitle ? <p className="text-[#64748b] text-xs mt-2">{subtitle}</p> : null}
        </div>
        {typeof trendPct === "number" ? <Trend pct={trendPct} /> : null}
      </div>
      <div className="mt-4 h-1 rounded-full" style={{ background: `${color}25` }}>
        <div className="h-1 rounded-full" style={{ width: "60%", background: color }} />
      </div>
    </div>
  );
}

function ConversionFunnel({ data }) {
  if (!data) return null;
  const steps = [
    { label: "Leads", value: data.leads, color: "#F59E0B" },
    { label: "Clients", value: data.clients, color: "#0099CC" },
    { label: "Propositions", value: data.proposals, color: "#8B5CF6" },
    { label: "Envoyees", value: data.proposals_sent, color: "#06B6D4" },
    { label: "Signees", value: data.proposals_signed, color: "#10B981" },
  ];

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6" data-testid="conversion-funnel">
      <div className="flex items-center gap-3 mb-6">
        <Target className="h-5 w-5 text-[#0099CC]" />
        <div>
          <p className="text-white font-semibold">Funnel de conversion</p>
          <p className="text-sm text-[#94A3B8]">Lead &rarr; Client &rarr; Proposition &rarr; Signature</p>
        </div>
      </div>

      {/* Funnel steps */}
      <div className="flex items-center justify-between gap-1 mb-6">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-1 flex-1">
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-xs text-[#94A3B8] mt-1">{s.label}</p>
              <div className="mt-2 h-1.5 rounded-full mx-auto" style={{ backgroundColor: `${s.color}30` }}>
                <div className="h-1.5 rounded-full transition-all" style={{
                  backgroundColor: s.color,
                  width: `${Math.max(10, (s.value / Math.max(steps[0].value, 1)) * 100)}%`
                }} />
              </div>
            </div>
            {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-[#64748b] shrink-0 mt-[-16px]" />}
          </div>
        ))}
      </div>

      {/* Conversion rates */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0B0F19] rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-[#0099CC]">{data.conversion_lead_to_client}%</p>
          <p className="text-xs text-[#64748b]">Lead &rarr; Client</p>
        </div>
        <div className="bg-[#0B0F19] rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-[#8B5CF6]">{data.conversion_proposal_to_signed}%</p>
          <p className="text-xs text-[#64748b]">Proposition &rarr; Signee</p>
        </div>
        <div className="bg-[#0B0F19] rounded-lg p-3 text-center">
          <p className="text-lg font-bold text-[#10B981]">{data.conversion_lead_to_signed}%</p>
          <p className="text-xs text-[#64748b]">Lead &rarr; Signee</p>
        </div>
      </div>
    </div>
  );
}

function FinancialSection({ fin }) {
  const barData = (fin.revenue_per_plan || []).map((p, i) => ({
    name: p.plan_name,
    revenue: p.monthly_revenue,
    clients: p.client_count,
    fill: PLAN_COLORS[i % PLAN_COLORS.length],
  }));

  return (
    <div className="space-y-6" data-testid="financial-kpis-section">
      <div className="flex items-center gap-3">
        <Euro className="h-5 w-5 text-[#0099CC]" />
        <h2 className="text-lg font-semibold text-white">Finance & Revenus</h2>
        <span className="text-xs text-[#64748b]">TVA non applicable, art. 293 B du CGI</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5" data-testid="kpi-mrr">
          <p className="text-[#94A3B8] text-xs uppercase tracking-wider">MRR</p>
          <p className="text-2xl font-bold text-[#0099CC] mt-1">
            {fin.mrr.toLocaleString("fr-FR")} <span className="text-sm font-normal">EUR</span>
          </p>
          <p className="text-xs text-[#64748b] mt-1">Revenu mensuel recurrent</p>
        </div>
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5" data-testid="kpi-arr">
          <p className="text-[#94A3B8] text-xs uppercase tracking-wider">ARR</p>
          <p className="text-2xl font-bold text-[#10B981] mt-1">
            {fin.arr.toLocaleString("fr-FR")} <span className="text-sm font-normal">EUR</span>
          </p>
          <p className="text-xs text-[#64748b] mt-1">Revenu annuel recurrent</p>
        </div>
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5" data-testid="kpi-churn">
          <p className="text-[#94A3B8] text-xs uppercase tracking-wider">Churn Rate</p>
          <p className={`text-2xl font-bold mt-1 ${fin.churn_rate > 5 ? "text-[#EF4444]" : "text-[#10B981]"}`}>
            {fin.churn_rate}%
          </p>
          <p className="text-xs text-[#64748b] mt-1">{fin.active_subscriptions} abonnements actifs</p>
        </div>
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5" data-testid="kpi-overdue">
          <p className="text-[#94A3B8] text-xs uppercase tracking-wider">Overdue</p>
          <p className={`text-2xl font-bold mt-1 ${fin.overdue.count > 0 ? "text-[#F59E0B]" : "text-[#10B981]"}`}>
            {fin.overdue.count}
          </p>
          <p className="text-xs text-[#64748b] mt-1">
            {fin.overdue.amount > 0
              ? `${fin.overdue.amount.toLocaleString("fr-FR")} EUR en retard`
              : "Aucune facture en retard"}
          </p>
        </div>
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5" data-testid="kpi-total-revenue">
          <p className="text-[#94A3B8] text-xs uppercase tracking-wider">Total Revenus</p>
          <p className="text-2xl font-bold text-white mt-1">
            {fin.total_revenue.toLocaleString("fr-FR")} <span className="text-sm font-normal text-[#94A3B8]">EUR</span>
          </p>
          <p className="text-xs text-[#64748b] mt-1">Factures payees</p>
        </div>
      </div>

      {/* Revenue per plan chart */}
      {barData.length > 0 && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6" data-testid="revenue-per-plan-chart">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-[#0099CC]" />
            <p className="text-white font-semibold">Revenus par plan</p>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <CartesianGrid stroke="#2A3441" strokeDasharray="4 4" />
                <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: "#0B0F19",
                    border: "1px solid #2A3441",
                    color: "#fff",
                    borderRadius: "8px",
                  }}
                  formatter={(value) => [`${value.toLocaleString("fr-FR")} EUR`, "Revenu mensuel"]}
                />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-4 mt-3">
            {barData.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                <div className="w-3 h-3 rounded-sm" style={{ background: p.fill }} />
                {p.name} ({p.clients} client{p.clients > 1 ? "s" : ""})
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_ICONS = {
  development: Code2,
  staging: Eye,
  production: Globe,
  archived: Box,
};

const LATE_COLORS = {
  watch: { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", border: "border-[#F59E0B]/30", label: "A surveiller" },
  late: { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]", border: "border-[#EF4444]/30", label: "En retard" },
  critical: { bg: "bg-[#DC2626]/10", text: "text-[#DC2626]", border: "border-[#DC2626]/30", label: "Critique" },
};

function PccWidget({ data }) {
  if (!data || data.total === 0) return null;

  const allLate = [
    ...(data.late_projects?.critical || []).map(p => ({ ...p, level: "critical" })),
    ...(data.late_projects?.late || []).map(p => ({ ...p, level: "late" })),
    ...(data.late_projects?.watch || []).map(p => ({ ...p, level: "watch" })),
  ];

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6" data-testid="pcc-kpi-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <FolderGit2 className="h-5 w-5 text-[#0099CC]" />
          <div>
            <p className="text-white font-semibold">Project Control Center</p>
            <p className="text-sm text-[#94A3B8]">{data.total} projet{data.total > 1 ? "s" : ""} &middot; Sync auto: 6h</p>
          </div>
        </div>
        {data.last_auto_sync?.completed_at && (
          <span className="text-xs text-[#64748b] flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Derniere sync: {new Date(data.last_auto_sync.completed_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
          </span>
        )}
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatusMini label="Total" value={data.total} color="#0099CC" />
        <StatusMini label="Developpement" value={data.by_status?.development || 0} color="#F59E0B" />
        <StatusMini label="Staging / Test" value={data.by_status?.staging || 0} color="#8B5CF6" />
        <StatusMini label="Production" value={data.by_status?.production || 0} color="#10B981" />
        <StatusMini label="Archive" value={data.by_status?.archived || 0} color="#64748B" />
      </div>

      {/* Late projects */}
      {allLate.length > 0 && (
        <div className="mb-5">
          <p className="text-xs text-[#64748b] uppercase tracking-wider mb-2 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-[#F59E0B]" /> Alertes projets ({allLate.length})
          </p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {allLate.slice(0, 5).map(p => {
              const c = LATE_COLORS[p.level];
              return (
                <div key={p.id} className={`${c.bg} border ${c.border} rounded-lg px-3 py-2 flex items-center justify-between`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[10px] font-bold uppercase ${c.text} shrink-0`}>{c.label}</span>
                    <span className="text-white text-sm truncate">{p.project_name}</span>
                  </div>
                  <div className="text-xs text-[#94A3B8] shrink-0 ml-2">
                    {p.reasons?.[0] || ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent synced */}
      {data.recent_synced?.length > 0 && (
        <div>
          <p className="text-xs text-[#64748b] uppercase tracking-wider mb-2 flex items-center gap-1">
            <GitBranch className="h-3 w-3" /> Derniers syncs GitHub
          </p>
          <div className="space-y-1.5">
            {data.recent_synced.slice(0, 4).map(p => (
              <div key={p.id} className="flex items-center gap-3 text-xs bg-[#0B0F19] rounded-lg px-3 py-2">
                <span className="text-white font-medium truncate flex-1">{p.project_name}</span>
                {p.last_commit && (
                  <span className="text-[#64748b] truncate max-w-[180px]">{p.last_commit.message?.substring(0, 40)}</span>
                )}
                <span className="text-[#94A3B8] shrink-0">
                  {p.last_synced_at ? new Date(p.last_synced_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusMini({ label, value, color }) {
  return (
    <div className="bg-[#0B0F19] rounded-lg p-3 text-center">
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[10px] text-[#64748b] mt-0.5">{label}</p>
    </div>
  );
}

export default function KPIDashboardPage() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [pccData, setPccData] = useState(null);

  useEffect(() => {
    fetchKpi();
  }, [period, isAdmin]);

  const fetchKpi = async () => {
    setLoading(true);
    try {
      const url = isAdmin ? `${API}/kpi/admin/overview?period=${period}` : `${API}/kpi/client/overview?period=${period}`;
      const res = await axios.get(url);
      setData(res.data);

      if (isAdmin) {
        try {
          const [finRes, funnelRes, pccRes] = await Promise.all([
            axios.get(`${API}/kpi/admin/financial`),
            axios.get(`${API}/kpi/conversion-funnel`),
            axios.get(`${API}/github/projects/kpi`),
          ]);
          setFinData(finRes.data);
          setFunnelData(funnelRes.data);
          setPccData(pccRes.data);
        } catch {
          setFinData(null);
          setFunnelData(null);
          setPccData(null);
        }
      }
    } catch {
      toast.error("Erreur KPI");
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    if (!data?.kpis) return [];
    if (isAdmin) {
      return [
        {
          name: "P-1",
          leads: data.kpis.leads?.previous ?? 0,
          contracts: data.kpis.contracts_signed?.previous ?? 0,
          conversion: data.kpis.conversion_rate?.previous ?? 0,
        },
        {
          name: "P",
          leads: data.kpis.leads?.value ?? 0,
          contracts: data.kpis.contracts_signed?.value ?? 0,
          conversion: data.kpis.conversion_rate?.value ?? 0,
        },
      ];
    }
    return [
      {
        name: "P",
        tickets: data.kpis.tickets_open?.value ?? 0,
        signed_docs: data.kpis.documents_signed?.value ?? 0,
      },
    ];
  }, [data, isAdmin]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kpi-dashboard">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">KPI Dashboard</h1>
          <p className="text-[#94A3B8] mt-1">
            {isAdmin ? "Pilotage business agence" : "Synthese de votre activite"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="bg-[#151B2B] border-[#2A3441] text-white w-[140px]">
              <SelectValue placeholder="Periode" />
            </SelectTrigger>
            <SelectContent className="bg-[#151B2B] border-[#2A3441]">
              <SelectItem value="7d">7 jours</SelectItem>
              <SelectItem value="30d">30 jours</SelectItem>
              <SelectItem value="90d">90 jours</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={fetchKpi}
            className="border-[#2A3441] text-[#94A3B8] hover:text-white"
            data-testid="refresh-kpi-btn"
          >
            Rafraichir
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : !data ? (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-10 text-center text-[#94A3B8]">
          KPI indisponibles
        </div>
      ) : (
        <>
          {/* Business KPI Cards */}
          {isAdmin ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KpiCard title="Clients actifs" value={data.kpis.clients_active.value} color="#0099CC" />
              <KpiCard title="Projets actifs" value={data.kpis.projects_active.value} color="#10B981" />
              <KpiCard
                title="Leads"
                value={data.kpis.leads.value}
                trendPct={data.kpis.leads.change_pct}
                subtitle={`Periode precedente: ${data.kpis.leads.previous}`}
                color="#F59E0B"
              />
              <KpiCard
                title="Conversion"
                value={`${data.kpis.conversion_rate.value.toFixed(1)}%`}
                trendPct={data.kpis.conversion_rate.change_pct}
                color="#8B5CF6"
              />
              <KpiCard
                title="Contrats signes"
                value={data.kpis.contracts_signed.value}
                trendPct={data.kpis.contracts_signed.change_pct}
                color="#22C55E"
              />
              <KpiCard title="Tickets ouverts" value={data.kpis.tickets_open.value} color="#EF4444" />
              <KpiCard title="Factures pending" value={data.kpis.invoices_pending.value} color="#F97316" />
              <KpiCard title="Factures payees" value={data.kpis.invoices_paid.value} color="#3B82F6" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KpiCard title="Tickets ouverts" value={data.kpis.tickets_open.value} color="#F59E0B" />
              <KpiCard title="Documents signes" value={data.kpis.documents_signed.value} color="#10B981" />
              <KpiCard title="Factures pending" value={data.kpis.invoices_pending.value} color="#F97316" />
              <KpiCard title="Factures payees" value={data.kpis.invoices_paid.value} color="#3B82F6" />
              <KpiCard title="Demandes envoyees" value={data.kpis.demandes_envoyees.value} color="#0099CC" />
            </div>
          )}

          {/* Financial KPIs (Admin Only) */}
          {isAdmin && finData && <FinancialSection fin={finData} />}

          {/* Conversion Funnel (Admin Only) */}
          {isAdmin && funnelData && <ConversionFunnel data={funnelData} />}

          {/* Project Control Center Widget (Admin Only) */}
          {isAdmin && pccData && <PccWidget data={pccData} />}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">Tendances</p>
                  <p className="text-sm text-[#94A3B8]">Comparaison periode vs periode precedente</p>
                </div>
                <Badge className="bg-[#0099CC]/10 text-[#0099CC] border-0">{period}</Badge>
              </div>
              <div className="h-[280px] mt-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="#2A3441" strokeDasharray="4 4" />
                    <XAxis dataKey="name" stroke="#94A3B8" />
                    <YAxis stroke="#94A3B8" />
                    <Tooltip
                      contentStyle={{
                        background: "#0B0F19",
                        border: "1px solid #2A3441",
                        color: "#fff",
                      }}
                    />
                    {isAdmin ? (
                      <>
                        <Line type="monotone" dataKey="leads" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="contracts" stroke="#22C55E" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="conversion" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                      </>
                    ) : (
                      <>
                        <Line type="monotone" dataKey="tickets" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="signed_docs" stroke="#10B981" strokeWidth={2} dot={false} />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="lg:col-span-4 bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
              <p className="text-white font-semibold">Alertes</p>
              <p className="text-sm text-[#94A3B8]">Signaux a surveiller</p>
              <div className="mt-6 space-y-3">
                {data.alerts?.length ? (
                  data.alerts.map((a, idx) => (
                    <div
                      key={idx}
                      className={`rounded-xl p-4 border ${
                        a.type === "danger"
                          ? "border-[#EF4444]/30 bg-[#EF4444]/10"
                          : "border-[#F59E0B]/30 bg-[#F59E0B]/10"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={a.type === "danger" ? "h-5 w-5 text-[#EF4444]" : "h-5 w-5 text-[#F59E0B]"} />
                        <div>
                          <p className="text-white font-medium">{a.title}</p>
                          <p className="text-sm text-[#94A3B8] mt-1">{a.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-xl p-4 border border-[#2A3441] bg-[#0B0F19]">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-[#94A3B8]" />
                      <div>
                        <p className="text-white font-medium">RAS</p>
                        <p className="text-sm text-[#94A3B8] mt-1">Aucune alerte sur la periode.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
